import React, {
  createContext,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { useOutlet } from "react-router";

type CustomOutletContextProps = {
  state: "hidden" | "hide" | "show";
  delay: number;
  setDelay: (value: number) => void;
};

export const CustomOutletContext =
  createContext<CustomOutletContextProps | null>(null);

export const CustomOutlet: React.FC = () => {
  const outlet = useOutlet();

  const storedOutlet = useRef<React.ReactElement | null>(null);

  const [state, setState] = useState<"hidden" | "hide" | "show">("hidden");
  const [delay, setDelay] = useState(0);

  const context = useContext(CustomOutletContext);

  let resultDelay = delay;
  let resultSetDelay: (value: number) => void = setDelay;

  if (context !== null) {
    resultDelay = context.delay;
    resultSetDelay = context.setDelay;
  }

  if (outlet !== null) {
    storedOutlet.current = outlet;
  }

  if (outlet !== null && state === "hidden") {
    setState("show");
  }

  if (outlet == null && state === "show") {
    setState("hide");

    setTimeout(() => {
      storedOutlet.current = null;
      setState("hidden");
    }, resultDelay);
  }

  let resultState = state;

  if (context !== null && context?.state !== "show") {
    resultState = context?.state;
  }

  const contextValue = useMemo(
    () => ({
      state: resultState,
      delay: resultDelay,
      setDelay: resultSetDelay,
    }),
    [resultDelay, resultSetDelay, resultState]
  );

  return (
    <CustomOutletContext.Provider value={contextValue}>
      {storedOutlet.current}
    </CustomOutletContext.Provider>
  );
};
